import { useCallback, useMemo, useState } from 'react';
import {
    getSongTiktokAudioStatsV2,
    GetSongTiktokAudioStatsV2Params,
    getTiktokAudioAssets,
    TiktokAudioAsset,
    usePaginatedFetch,
    ClientMonitoredTiktokAudioRelationship,
    getClientMonitoredTiktokAudioRelationships,
} from '@round/api';
import { GenericDropdownOption } from '../../../../../App.types';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { TopTiktokAudiosTableRow } from '../SongDetailsModal.types';
import { TopTiktokAudiosTableProps } from './TopTiktokAudiosTable';
import { ValueType } from 'react-select';

const sortingOptions: GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

type UseTopTiktokAudiosReturn = {
    refresh: (requestInit?: RequestInit) => Promise<void>;
    getTopTiktokAudiosTableProps: () => Omit<TopTiktokAudiosTableProps, 'onRemoveAudioClicked'>;
};

export default function useTopTiktokAudios(songId: number | null): UseTopTiktokAudiosReturn {
    const fetchSongTopTiktokAudios = useCallback(
        async (params: GetSongTiktokAudioStatsV2Params, requestInit?: RequestInit) => {
            if (!songId) {
                throw new Error('No song passed');
            }

            return getSongTiktokAudioStatsV2(songId, params, requestInit);
        },
        [songId]
    );

    const [search, setSearch] = useState('');
    const [order, setOrder] = useState<GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>>(
        sortingOptions[1]
    );
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [tableLoading, setTableLoading] = useState(false);

    const {
        page: stats,
        getCurrentPage,
        count,
    } = usePaginatedFetch(fetchSongTopTiktokAudios, {
        search,
        page,
        page_size: pageSize,
        ordering: order?.value,
    });

    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);
    const [clientMonitoredTiktokAudioRelationships, setClientMonitoredTiktokAudioRelationships] = useState<
        ClientMonitoredTiktokAudioRelationship[]
    >([]);

    const initTable = useCallback(
        async (requestInit?: RequestInit) => {
            if (typeof songId === 'number') {
                setTableLoading(true);
                getCurrentPage(requestInit).catch(() => setTableLoading(false));
            }
        },
        [getCurrentPage, songId]
    );

    useAbortableEffect(
        (signal) => {
            initTable({ signal });
        },
        [initTable]
    );

    useAbortableEffect(
        (signal) => {
            if (!stats.length) {
                setTableLoading(false);
                return;
            }

            const audioIds = stats.map((s) => s.music_id);
            const monitoredMusicIds = stats.map((s) => s.monitored_music_id);

            Promise.all([
                getTiktokAudioAssets(audioIds, { signal }).then(setAssets),
                getClientMonitoredTiktokAudioRelationships({
                    page: 1,
                    page_size: monitoredMusicIds.length,
                    monitored_audio_id: monitoredMusicIds.toString(),
                }).then((response) => {
                    if (response.status === 200) {
                        setClientMonitoredTiktokAudioRelationships(response.data.results);
                    }
                }),
            ])
                .catch(() => {})
                .finally(() => setTableLoading(false));
        },
        [stats]
    );

    const rows: TopTiktokAudiosTableRow[] = useMemo(
        () =>
            stats.map((stats) => {
                const asset = assets.find((asset) => asset.audio_id === stats.music_id);
                const rel = clientMonitoredTiktokAudioRelationships.filter(
                    (rel) => rel.monitored_audio_id === stats.monitored_music_id
                );

                return {
                    ...stats,
                    imageUrl: asset?.image_thumb.cached_url || asset?.image_thumb.original_url,
                    audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url,
                    clientMonitoredAudioRelation: rel,
                    dailyGrowth: stats.standardised_data.daily_video_counts.slice(-7).reduce(
                        (acc, current) => {
                            acc.push({
                                timestamp: current.timestamp,
                                value: current.change,
                            });

                            return acc;
                        },
                        [] as TopTiktokAudiosTableRow['dailyGrowth']
                    ),
                };
            }),
        [assets, clientMonitoredTiktokAudioRelationships, stats]
    );

    return useMemo(
        () => ({
            refresh: initTable,
            getTopTiktokAudiosTableProps: () => ({
                loading: tableLoading,
                sortingOptions,
                count,
                order,
                setOrder: (
                    value: ValueType<GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>, false>
                ) => setOrder(value ?? sortingOptions[0]),
                setSearch: (search: string) => {
                    setPage(1);
                    setSearch(search);
                },
                search,
                rows,
                setPageSize: (value: number | undefined) => setPageSize(value ?? 10),
                setPage,
                pageSize,
                page,
            }),
        }),
        [count, initTable, order, page, pageSize, rows, search, tableLoading]
    );
}
